import { Card, Badge } from '@/core/ui/components';
import type { WebhookConfig } from '@yawi/types';
import { Webhook, Clock, AlertTriangle } from 'lucide-react';

interface WebhooksCardProps {
  webhooks: WebhookConfig[];
}

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  ACTIVE: { variant: 'success', label: 'Activo' },
  INACTIVE: { variant: 'default', label: 'Inactivo' },
  FAILED: { variant: 'error', label: 'Fallido' },
};

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return 'Nunca';

  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 60000) return 'Hace un momento';
  if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)}m`;
  if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
  if (diff < 604800000) return `Hace ${Math.floor(diff / 86400000)}d`;
  return date.toLocaleDateString();
}

export function WebhooksCard({ webhooks }: WebhooksCardProps) {
  if (!webhooks || webhooks.length === 0) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
          <Webhook className="w-5 h-5" />
          Webhooks
        </h3>
        <p className="text-theme-secondary text-center py-4">
          Sin webhooks configurados
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h3 className="text-lg font-semibold text-theme mb-4 flex items-center gap-2">
        <Webhook className="w-5 h-5" />
        Webhooks ({webhooks.length})
      </h3>

      <div className="space-y-3">
        {webhooks.map((webhook) => {
          const statusBadge = STATUS_BADGES[webhook.status] || STATUS_BADGES.ACTIVE;
          const hasFailures = webhook.failureCount > 0;

          return (
            <div
              key={webhook.id}
              className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Webhook className="w-4 h-4 text-indigo-600 dark:text-indigo-400 shrink-0" />
                  <span className="font-medium text-theme truncate">
                    {webhook.name || 'Sin nombre'}
                  </span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {hasFailures && (
                    <Badge variant="warning" className="text-xs flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {webhook.failureCount}
                    </Badge>
                  )}
                  <Badge variant={statusBadge.variant} className="text-xs">
                    {statusBadge.label}
                  </Badge>
                </div>
              </div>

              <p className="text-xs text-theme-secondary font-mono truncate mb-2">
                {webhook.url}
              </p>

              <div className="flex items-center justify-between text-xs text-theme-secondary">
                <div className="flex flex-wrap gap-1">
                  {webhook.events.slice(0, 2).map((event) => (
                    <Badge key={event} variant="default" className="text-xs">
                      {event}
                    </Badge>
                  ))}
                  {webhook.events.length > 2 && (
                    <Badge variant="default" className="text-xs">
                      +{webhook.events.length - 2}
                    </Badge>
                  )}
                </div>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatRelativeTime(webhook.lastTriggeredAt)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
